import { Box, Paper, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useInsightsDashboard } from '../../../contexts/InsightsDashboardContext';
import type { AggregationData, StrategicInsightsData } from '../../../services/tenderAIService';

interface ProposalMetricsProps {
  data: StrategicInsightsData | null;
  sourceLabels?: Record<string, string>;
}

const ProposalMetrics = ({ data, sourceLabels = {} }: ProposalMetricsProps) => {
  const theme = useTheme();
  const { state, dispatch } = useInsightsDashboard();

  if (!data?.aggregations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">No data available for the selected filters.</Typography>
      </Box>
    );
  }

  const { sector, client, source, team } = data.aggregations;

  // Helper to extract and sort data by Win Rate descending to highlight highest success ratios
  const transformData = (
    aggregation: Record<string, AggregationData> | undefined,
    labelsMap?: Record<string, string>
  ) => {
    if (!aggregation) {
      return {
        labels: [],
        rawLabels: [],
        wins: [],
        active: [],
        other: [],
        losses: [],
        winRates: [],
      };
    }

    const entries = Object.entries(aggregation)
      .filter(([_, agg]) => agg.bidVolume > 0)
      .sort((a, b) => {
        // Primary sort: win rate, secondary: bid volume
        if (b[1].winRate !== a[1].winRate) {
          return b[1].winRate - a[1].winRate;
        }
        return b[1].bidVolume - a[1].bidVolume;
      });

    return {
      labels: entries.map(([key]) => {
        const mapped = labelsMap ? labelsMap[key] || key : key;
        return mapped.length > 25 ? `${mapped.substring(0, 22)}...` : mapped;
      }),
      rawLabels: entries.map(([key]) => key), // Keep raw for filtering
      wins: entries.map(([_, agg]) => agg.wins),
      active: entries.map(([_, agg]) => agg.active),
      other: entries.map(([_, agg]) => agg.other),
      losses: entries.map(([_, agg]) => agg.losses),
      winRates: entries.map(([_, agg]) => agg.winRate),
    };
  };

  const sectorData = transformData(sector);
  const clientData = transformData(client);
  const sourceData = transformData(source, sourceLabels);
  const teamData = transformData(team);

  const hasData =
    sectorData.labels.length > 0 ||
    clientData.labels.length > 0 ||
    sourceData.labels.length > 0 ||
    teamData.labels.length > 0;

  if (!hasData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">
          No proposal metrics to display for the selected filters.
        </Typography>
      </Box>
    );
  }

  const handleFilterToggle = (
    type: 'SET_SECTORS' | 'SET_CLIENTS' | 'SET_SOURCES' | 'SET_TEAMS',
    currentValues: string[],
    value: string
  ) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    dispatch({ type, payload: newValues });
  };

  const renderStackedChart = (
    title: string,
    axisLabel: string,
    filterType: 'SET_SECTORS' | 'SET_CLIENTS' | 'SET_SOURCES' | 'SET_TEAMS',
    filterValues: string[],
    dataObj: {
      labels: string[];
      rawLabels: string[];
      wins: number[];
      active: number[];
      losses: number[];
      other: number[];
      winRates: number[];
    }
  ) => {
    if (dataObj.labels.length === 0) {
      return (
        <Paper
          variant="outlined"
          sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 250 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No data
            </Typography>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 300 }}
      >
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
          <BarChart
            xAxis={[
              {
                scaleType: 'band',
                data: dataObj.labels,
                label: axisLabel,
                height: 120,
                tickLabelStyle: {
                  angle: 45,
                  fontSize: 11,
                },
                tickPlacement: 'middle',
                tickLabelPlacement: 'tick',
              },
            ]}
            yAxis={[{ label: 'Number of Tenders' }]}
            series={[
              {
                id: 'wins',
                data: dataObj.wins,
                stack: 'volume',
                label: 'Wins',
                color: theme.palette.success.main,
                barLabel: 'value',
              },
              {
                id: 'active',
                data: dataObj.active,
                stack: 'volume',
                label: 'Active',
                color: theme.palette.info.main,
                barLabel: 'value',
              },
              {
                id: 'other',
                data: dataObj.other,
                stack: 'volume',
                label: 'Other',
                color: theme.palette.warning.main,
                barLabel: 'value',
              },
              {
                id: 'losses',
                data: dataObj.losses,
                stack: 'volume',
                label: 'Losses',
                color: theme.palette.error.main,
                barLabel: 'value',
              },
            ]}
            margin={{ left: 10, right: 10, top: 40, bottom: 10 }}
            sx={{
              '& .MuiBarLabel-series-wins': {
                fill: theme.palette.success.contrastText,
                fontWeight: 500,
              },
              '& .MuiBarLabel-series-active': {
                fill: theme.palette.info.contrastText,
                fontWeight: 500,
              },
              '& .MuiBarLabel-series-other': {
                fill: theme.palette.warning.contrastText,
                fontWeight: 500,
              },
              '& .MuiBarLabel-series-losses': {
                fill: theme.palette.error.contrastText,
                fontWeight: 500,
              },
            }}
            slotProps={{
              legend: {
                position: { vertical: 'top', horizontal: 'center' },
              },
            }}
            onItemClick={(_event, d) => {
              const item = d.dataIndex !== undefined ? dataObj.rawLabels[d.dataIndex] : undefined;
              if (item) {
                handleFilterToggle(filterType, filterValues, item);
              }
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 4,
        height: '100%',
      }}
    >
      {renderStackedChart('Win/Loss by Sector', 'Sector', 'SET_SECTORS', state.sectors, sectorData)}
      {renderStackedChart('Win/Loss by Client', 'Client', 'SET_CLIENTS', state.clients, clientData)}
      {renderStackedChart(
        'Win/Loss by Tender Website',
        'Website',
        'SET_SOURCES',
        state.sources,
        sourceData
      )}
      {renderStackedChart('Win/Loss by Team', 'Team', 'SET_TEAMS', state.teams, teamData)}
    </Box>
  );
};

export default ProposalMetrics;
